import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#3b82f6', // Blue from logo - Cloud track
      light: '#60a5fa',
      dark: '#2563eb',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#10b981', // Emerald green - Data track
      light: '#34d399',
      dark: '#059669',
      contrastText: '#ffffff',
    },
    background: {
      default: '#0f172a', // Deep blue background
      paper: '#1e293b', // Slate blue for cards
    },
    text: {
      primary: '#f1f5f9', // Light text
      secondary: '#94a3b8', // Muted gray text
    },
    divider: 'rgba(148, 163, 184, 0.1)', // Subtle gray dividers
    success: {
      main: '#10b981', // Green accent
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: '#f59e0b', // Orange
      light: '#fbbf24',
      dark: '#d97706',
    },
    info: {
      main: '#3b82f6', // Blue
      light: '#60a5fa',
      dark: '#2563eb',
    }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 800 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 700 },
    button: {
      textTransform: 'none',
      fontWeight: 700,
      letterSpacing: 0.2,
    },
    subtitle1: { fontWeight: 600 },
  },
  shape: { borderRadius: 20 },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @import url('https://fonts.googleapis.com/css2?family=Fira+Code:wght@300;400;500;600;700&family=JetBrains+Mono:wght@300;400;500;600;700&display=swap');

        body {
          background: radial-gradient(1200px 600px at 20% -10%, rgba(59,130,246,0.12), transparent),
                      radial-gradient(1000px 500px at 120% 10%, rgba(16,185,129,0.08), transparent),
                      radial-gradient(800px 400px at 50% 50%, rgba(245,158,11,0.04), transparent),
                      #0f172a;
          background-attachment: fixed;
        }

        code {
          font-family: 'Fira Code', 'JetBrains Mono', 'Consolas', monospace;
        }
      `
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(15, 23, 42, 0.85)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(59,130,246,0.3)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.4), 0 0 1px rgba(59,130,246,0.3)',
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          backgroundImage: 'linear-gradient(180deg, rgba(59,130,246,0.05), rgba(59,130,246,0.02))',
          border: '1px solid rgba(59,130,246,0.15)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(12px)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 0 0 rgba(0,0,0,0)',
          padding: '12px 28px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f97316 100%)',
          color: '#0a0a0a',
          boxShadow: '0 4px 20px rgba(245,158,11,0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #ea580c 100%)',
            boxShadow: '0 8px 32px rgba(245,158,11,0.5)',
            transform: 'translateY(-2px)',
          }
        },
        containedSecondary: {
          background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f97316 100%)',
          color: '#0a0a0a',
          boxShadow: '0 4px 20px rgba(245,158,11,0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #ea580c 100%)',
            boxShadow: '0 8px 32px rgba(245,158,11,0.5)',
            transform: 'translateY(-2px)',
          }
        },
        outlined: {
          borderRadius: 16,
          borderWidth: 2,
          borderColor: 'rgba(245,158,11,0.5)',
          color: '#fbbf24',
          '&:hover': {
            borderColor: 'rgba(245,158,11,0.8)',
            backgroundColor: 'rgba(245,158,11,0.1)',
            borderWidth: 2,
            transform: 'translateY(-2px)',
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          background: 'rgba(30, 41, 59, 0.4)',
          border: '1px solid rgba(148,163,184,0.1)',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(148,163,184,0.15), transparent)',
          },
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(148,163,184,0.2)',
          }
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backdropFilter: 'blur(12px)',
          border: '1px solid',
          padding: '12px 20px',
        },
        standardInfo: {
          backgroundColor: 'rgba(59,130,246,0.12)',
          borderColor: 'rgba(59,130,246,0.3)',
          color: '#dbeafe',
        },
        standardWarning: {
          backgroundColor: 'rgba(245,158,11,0.12)',
          borderColor: 'rgba(245,158,11,0.3)',
          color: '#fef3c7',
        },
        standardSuccess: {
          backgroundColor: 'rgba(16,185,129,0.12)',
          borderColor: 'rgba(16,185,129,0.3)',
          color: '#d1fae5',
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 16,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
            }
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }
      }
    }
  }
});

export default theme;

